/**
 * Shared dashboard value formatting utilities.
 * Centralizes rounding logic so every dashboard component displays
 * clean, consistent numbers (no raw floating-point noise).
 *
 * - Percentages: 1 decimal (77.8%)
 * - Currency: 2 decimals ($3.57)
 * - Multipliers: 2 decimals (1.46x)
 * - Counts: locale integer (12,345)
 * - Seconds: 1 decimal (4.6s)
 *
 * NOTE: This does NOT replace metricsService.ts formatting which handles
 * analysis-view metric display. This is for dashboard-level aggregates.
 */

/**
 * Format a percentage value with consistent rounding.
 * @param value - The numeric percentage value (e.g. 77.7777)
 * @param decimals - Number of decimal places (default 1)
 * @returns Formatted string like "77.8%" or "--" for null/undefined
 */
export function fmtPct(
  value: number | null | undefined,
  decimals = 1
): string {
  if (value === null || value === undefined) return '--';
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format a KPI value based on its unit type.
 * @param value - The numeric KPI value
 * @param unit - Unit string: '%', '$', 'x', '#', 's', or other
 * @returns Formatted string like "1.2%", "$3.57", "1.46x", "12,345", or "--"
 */
export function fmtKpiValue(
  value: number | null | undefined,
  unit: string
): string {
  if (value === null || value === undefined) return '--';

  switch (unit) {
    case '%':
      return `${value.toFixed(1)}%`;
    case '$':
      return `$${value.toFixed(2)}`;
    case 'x':
      return `${value.toFixed(2)}x`;
    case '#':
      return value.toLocaleString();
    case 's':
      return `${value.toFixed(1)}s`;
    default:
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
}

/**
 * Format a delta/change value. Always uses Math.abs internally.
 * @param value - The raw delta (positive or negative)
 * @param unit - Unit string: '%', '$', 'x', '#', 's', or other
 * @returns Formatted absolute delta string like "2.3%", "$0.12"
 */
export function fmtDelta(value: number, unit: string): string {
  const abs = Math.abs(value);

  switch (unit) {
    case '%':
      return `${abs.toFixed(1)}%`;
    case '$':
      return `$${abs.toFixed(2)}`;
    case 'x':
      return `${abs.toFixed(2)}x`;
    case '#':
      return abs.toLocaleString();
    case 's':
      return `${abs.toFixed(1)}s`;
    default:
      return abs.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
}
